import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BookOpen,
  GraduationCap,
  IndianRupee,
  Plus,
  Trash2,
  Calculator,
  AlertCircle,
} from "lucide-react";
import { courseGroupService, CreateCourseGroupWithYearwiseFeesData, YearWiseFee } from "@/services/courseGroupService";
import { formatCurrency } from "@/lib/model";

interface CreateCourseGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCourseGroupCreated: () => void;
}

const CreateCourseGroupDialog: React.FC<CreateCourseGroupDialogProps> = ({
  open,
  onOpenChange,
  onCourseGroupCreated,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    durationYears: 4,
    description: "",
  });
  
  const [yearWiseFees, setYearWiseFees] = useState<YearWiseFee[]>([
    { yearNumber: 1, feeAmount: 0 },
    { yearNumber: 2, feeAmount: 0 },
    { yearNumber: 3, feeAmount: 0 },
    { yearNumber: 4, feeAmount: 0 },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const validateForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.name.trim()) errors.push("Course name is required");
    if (!formData.code.trim()) errors.push("Course code is required");
    if (formData.durationYears < 1 || formData.durationYears > 10) {
      errors.push("Duration must be between 1 and 10 years");
    }
    
    // Validate year-wise fees
    const activeFees = yearWiseFees.slice(0, formData.durationYears);
    if (activeFees.some(fee => fee.feeAmount <= 0)) {
      errors.push("All year fees must be greater than 0");
    }
    
    return errors;
  };

  const calculateTotalFee = (): number => {
    return yearWiseFees
      .slice(0, formData.durationYears)
      .reduce((total, fee) => total + fee.feeAmount, 0);
  };

  const updateDurationYears = (duration: number) => {
    setFormData(prev => ({ ...prev, durationYears: duration }));
    
    // Update year-wise fees array
    const newYearWiseFees = [];
    for (let i = 1; i <= Math.max(duration, 10); i++) {
      const existingFee = yearWiseFees.find(f => f.yearNumber === i);
      newYearWiseFees.push({
        yearNumber: i,
        feeAmount: existingFee?.feeAmount || 0
      });
    }
    setYearWiseFees(newYearWiseFees);
  };

  const updateYearFee = (yearNumber: number, amount: number) => {
    setYearWiseFees(prev =>
      prev.map(fee =>
        fee.yearNumber === yearNumber
          ? { ...fee, feeAmount: Math.max(0, amount) }
          : fee
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (formErrors.length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const courseGroupData: CreateCourseGroupWithYearwiseFeesData = {
        name: formData.name.trim(),
        code: formData.code.toUpperCase().trim(),
        durationYears: formData.durationYears,
        description: formData.description.trim() || undefined,
        yearWiseFees: yearWiseFees.slice(0, formData.durationYears),
      };

      await courseGroupService.createCourseGroupWithYearwiseFees(courseGroupData);
      
      // Reset form
      setFormData({
        name: "",
        code: "",
        durationYears: 4,
        description: "",
      });
      setYearWiseFees([
        { yearNumber: 1, feeAmount: 0 },
        { yearNumber: 2, feeAmount: 0 },
        { yearNumber: 3, feeAmount: 0 },
        { yearNumber: 4, feeAmount: 0 },
      ]);
      
      onCourseGroupCreated();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error creating course group:", error);
      setErrors([error.response?.data?.message || "Failed to create course group. Please try again."]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalFee = calculateTotalFee();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-blue-600" />
            Create Course Group with Year-wise Fees
          </DialogTitle>
          <DialogDescription>
            Create a new course group with detailed year-wise fee structure. 
            Students will be locked to this fee structure at enrollment.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Course Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Course Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Bachelor of Computer Science"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Course Code *
                  </label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., BCS"
                    maxLength={10}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Duration (Years) *
                </label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={formData.durationYears}
                  onChange={(e) => updateDurationYears(parseInt(e.target.value) || 1)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Description
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the course..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Year-wise Fee Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <IndianRupee className="h-5 w-5" />
                Year-wise Fee Structure
              </CardTitle>
              <p className="text-sm text-gray-600">
                Set different fee amounts for each year of the course.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {yearWiseFees.slice(0, formData.durationYears).map((fee, index) => (
                  <motion.div
                    key={fee.yearNumber}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 border rounded-lg"
                  >
                    <Badge variant="outline" className="shrink-0">
                      Year {fee.yearNumber}
                    </Badge>
                    
                    <div className="flex-1">
                      <Input
                        type="number"
                        min={0}
                        step={100}
                        value={fee.feeAmount || ""}
                        onChange={(e) => updateYearFee(fee.yearNumber, parseFloat(e.target.value) || 0)}
                        placeholder="Enter fee amount"
                        className="text-right"
                      />
                    </div>
                    
                    <div className="text-sm font-medium text-green-600 min-w-[100px] text-right">
                      {fee.feeAmount > 0 ? formatCurrency(fee.feeAmount) : "₹0"}
                    </div>
                  </motion.div>
                ))}
                
                {/* Total Fee Display */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between text-lg font-semibold">
                    <div className="flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-blue-600" />
                      Total Course Fee
                    </div>
                    <div className="text-2xl text-green-600">
                      {formatCurrency(totalFee)}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    This fee structure will be locked for students at enrollment time.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Error Messages */}
          {errors.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900">Please fix the following errors:</h4>
                    <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Course Group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCourseGroupDialog;
